import axios from 'axios';
import cms from './cms';

const statusMap: { [key: string]: SubscriptionStatus } = {
  activa: 'successful',
  cancelada: 'error',
  vencido: 'pending',
};

type SubscriptionStatus = 'successful' | 'error' | 'pending';
type SubscriptionPeriodicity = 'ANUAL' | 'MENSUAL';

interface IProductType {
  id: number;
  name: string;
}

/** Company with param from API (e.g. product_id.company[]) */
export interface ICompanyWithParam {
  id: number;
  name: string;
  param?: string;
}

interface IProduct {
  amount: number;
  quantity: number;
  calculate_amount: boolean;
  product_id: {
    alias_title: string;
    is_addon: boolean;
    product_type_id: IProductType;
    company?: ICompanyWithParam[];
  };
}

export interface ICompany {
  id: number;
  name: string;
}

interface ISubscriptionResponse {
  id: number;
  provider_processor: string;
  version: number;
  date_init: string;
  date_expire: string;
  has_trial: boolean;
  stripe_subscription_id: string;
  amount: number;
  was_migrated: boolean;
  calculate_amount: boolean;
  status_subscription_id: {
    name: string;
  };
  periocity: {
    name: string;
  };
  productos_usuarios: IProduct[];
  payments: Payment[];
  date_cancel?: string | null;
  is_freemium?: boolean;
  company: ICompany;
}

export interface IProductDetails {
  type: string;
  isAddon: boolean;
  quantity: number;
  calculateAmount: boolean;
  amount: number;
}

/** True if any product in the subscription belongs to company param "sodaseguros" (seguro) */
export const isInsuranceSubscription = (
  productos_usuarios: IProduct[]
): boolean => {
  return productos_usuarios.some((item) =>
    (item.product_id.company ?? []).some(
      (c) => c.param?.toLowerCase() === 'sodaseguros'
    )
  );
};

export interface Subscription {
  id: string;
  dbId: number;
  version: number;
  products: string[];
  price: number;
  dateInit: string;
  dateInitRaw: string;
  dateExpire: string;
  dateExpireRaw: string;
  hasTrial: boolean;
  typePeriodicity: SubscriptionPeriodicity;
  status: SubscriptionStatus;
  productsDetails: IProductDetails[];
  payments: SubscriptionPayment[];
  providerProcessor: string;
  wasMigrated: boolean;
  calculateAmount: boolean;
  dateCancel?: string;
  isFreemium: boolean;
  company?: ICompany;
  /** Suscripción de tipo seguro (ej. Soda Seguros); no se muestran opciones de cancelación con/sin reembolso */
  isInsurance: boolean;
}

interface ProductDetailsViewResponse {
  has_trial: boolean;
  sfClientId: string;
  id: number;
  amount: number;
  quantity: number;
  calculate_amount: boolean;
  status: {
    name: 'successful' | 'error';
  };
  product_id: {
    id: number;
    alias_title: string;
    alias_product_type: string;
    product: {
      id: number;
      is_addon: boolean;
    };
    product_type_id: {
      id: number;
      name: string;
    };
    company?: ICompanyWithParam[];
  };
}

export interface SubscriptionPayment {
  id: number;
  coupon_amount: number;
  coupon_code: string;
  currency: {
    id: number;
    name: string;
    Symbol: string;
  };
  date: string;
  detail: string;
  has_discount: boolean;
  payment_method: {
    id: number;
    name: string;
  };
  payment_status: {
    id: number;
    name: string;
  };
  payment_type: {
    id: number;
    name: string;
  };
  provider: string;
  provider_reference_id: string;
  subtotal: number;
  total: number;
}

type Payment = SubscriptionPayment;

export interface IPayment {
  id: number;
  coupon_amount: number;
  coupon_code: string;
  currency: string;
  date: string;
  detail: string;
  has_discount: boolean;
  payment_method: string;
  payment_status: string;
  payment_type: string;
  provider: string;
  provider_reference_id: string;
  subtotal: number;
  total: number;
  subscription_id: string;
}

interface ISubscriptionWithProductsResponse {
  id: number;
  stripe_subscription_id: string;
  provider_processor: string;
  date_init: string;
  date_expire: string;
  amount: number;
  payments: Payment[];
  periocity: {
    name: string;
  };
  user_id: {
    customer_stripe_id: string;
  };
  productos_usuarios: ProductDetailsViewResponse[];
}

export interface ProductsDetailsView {
  userProductIdPublic: string;
  productId: number;
  productName: string;
  quantity: number;
  hasTrial: boolean;
  amount: number;
  status: 'successful' | 'error' | 'pending';
  aliasProductType: string;
  isAddon: boolean;
  type: string;
  calculateAmount: boolean;
}

/** True if any product has company param "sodaseguros" (seguro) */
const isInsuranceFromDetails = (
  productos_usuarios: ProductDetailsViewResponse[]
): boolean =>
  productos_usuarios.some((item) =>
    (item.product_id.company ?? []).some(
      (c) => c.param?.toLowerCase() === 'sodaseguros'
    )
  );

export interface SubscriptionWithProducts {
  id: string;
  dbId: number;
  amount: number;
  products: ProductsDetailsView[];
  dateInit: string;
  dateExpire: string;
  typePeriodicity: SubscriptionPeriodicity;
  customerStripeId: string;
  providerProcessor: string;
  isInsurance: boolean;
}

const formatDate = (date_init: string): string => {
  const date = new Date(date_init);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const extractTitleProducts = (productos_usuarios: IProduct[]): string[] => {
  return productos_usuarios.map((product) => product.product_id.alias_title);
};

const formatPeriodicity = (
  periodicityName: string
): SubscriptionPeriodicity => {
  const upperName = periodicityName.toUpperCase();
  if (
    upperName !== 'ANUAL' &&
    upperName !== 'MENSUAL' &&
    upperName !== 'FREEMIUM'
  ) {
    throw new Error(`Periodicidad inválida: ${periodicityName}`);
  }
  return upperName as SubscriptionPeriodicity;
};

const formatStatus = (statusName: string): SubscriptionStatus => {
  return statusMap[statusName.toLowerCase()] || 'error';
};

const formatSubscriptionResponse = (
  response: ISubscriptionResponse[]
): Subscription[] => {
  if (!Array.isArray(response)) {
    throw new Error('La respuesta debe ser un array');
  }

  return response.map((subscription) => {
    if (!subscription || typeof subscription !== 'object') {
      throw new Error('Elemento de suscripción inválido');
    }
    const {
      id,
      amount,
      date_init,
      date_expire,
      status_subscription_id,
      periocity,
      productos_usuarios,
      stripe_subscription_id,
      version,
      has_trial,
      payments,
      provider_processor,
      was_migrated,
      calculate_amount,
      date_cancel,
      is_freemium,
      company,
    } = subscription;

    if (
      !id ||
      !version ||
      !date_init ||
      !date_expire ||
      !stripe_subscription_id ||
      !periocity?.name ||
      !status_subscription_id?.name ||
      !provider_processor
    ) {
      throw new Error('Datos de suscripción incompletos');
    }

    return {
      id: stripe_subscription_id,
      dbId: id,
      version,
      dateInit: formatDate(date_init),
      dateInitRaw: date_init,
      dateExpire: formatDate(date_expire),
      dateExpireRaw: date_expire,
      products: extractTitleProducts(productos_usuarios),
      typePeriodicity: formatPeriodicity(periocity.name),
      status: formatStatus(status_subscription_id.name),
      price: amount,
      productsDetails: formatProductsDetails(productos_usuarios),
      hasTrial: !!has_trial,
      payments: payments,
      providerProcessor: provider_processor,
      wasMigrated: !!was_migrated,
      calculateAmount: !!calculate_amount,
      dateCancel: date_cancel ?? undefined,
      isFreemium: !!is_freemium,
      company: company,
      isInsurance: isInsuranceSubscription(productos_usuarios),
    };
  });
};

const formatProductsDetails = (productos_usuarios: IProduct[]) => {
  return productos_usuarios.map((product) => ({
    type: product.product_id.product_type_id.name.toLowerCase(),
    isAddon: product.product_id.is_addon,
    quantity: product.quantity,
    calculateAmount: !!product.calculate_amount,
    amount: product.amount,
  }));
};

const formatProducts = (
  productos_usuarios: ProductDetailsViewResponse[]
): ProductsDetailsView[] => {
  return (
    productos_usuarios.map((product) => ({
      userProductIdPublic: product.sfClientId,
      productId: product.id,
      productName: product.product_id?.alias_title,
      aliasProductType: product.product_id?.alias_product_type || 'propiedad',
      quantity: product.quantity,
      hasTrial: !!product.has_trial,
      amount: product.amount,
      status: formatStatus(product.status.name),
      isAddon: !!product?.product_id?.product?.is_addon,
      type: product.product_id.product_type_id.name.toLowerCase(),
      calculateAmount: !!product.calculate_amount,
    })) || []
  );
};

const formatSubscriptionWithProductsResponse = (
  sub: ISubscriptionWithProductsResponse
): SubscriptionWithProducts => {
  const {
    id,
    amount,
    date_init,
    date_expire,
    stripe_subscription_id,
    provider_processor,
    periocity,
    user_id,
    productos_usuarios,
  } = sub;

  if (
    !id ||
    !date_init ||
    !date_expire ||
    !stripe_subscription_id ||
    !provider_processor ||
    !periocity?.name ||
    !user_id?.customer_stripe_id ||
    !Array.isArray(productos_usuarios)
  ) {
    throw new Error('Datos de los detalles de la suscripción incompletos');
  }

  return {
    id: stripe_subscription_id,
    dbId: id,
    dateInit: formatDate(date_init),
    dateExpire: formatDate(date_expire),
    amount: amount,
    typePeriodicity: formatPeriodicity(periocity.name),
    products: formatProducts(productos_usuarios),
    customerStripeId: user_id.customer_stripe_id,
    providerProcessor: provider_processor,
    isInsurance: isInsuranceFromDetails(productos_usuarios),
  };
};

const formatPayments = (
  response: ISubscriptionWithProductsResponse[]
): IPayment[] => {
  const paymentsNew: IPayment[] = [];
  response.forEach((subscription) => {
    subscription.payments.forEach((payment) => {
      paymentsNew.push({
        id: payment.id,
        coupon_amount: payment.coupon_amount,
        coupon_code: payment.coupon_code,
        currency: payment.currency.name,
        detail: payment.detail,
        has_discount: payment.has_discount,
        payment_method: payment.payment_method.name,
        payment_status: payment.payment_status.name,
        payment_type: payment.payment_type.name,
        total: payment.total,
        subtotal: payment.subtotal,
        date: payment.date,
        provider: payment.provider,
        provider_reference_id: payment.provider_reference_id,
        subscription_id: subscription.stripe_subscription_id,
      });
    });
  });

  return paymentsNew.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });
};

export async function getUserSubscriptions(
  userId: number
): Promise<{ subscriptions: Subscription[]; payments: IPayment[] }> {
  try {
    const response = await cms.get(
      `/user-subscriptions/user-products/${userId}`
    );

    return {
      subscriptions: formatSubscriptionResponse(response.data),
      payments: formatPayments(response.data || []),
    };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get subscriptions 🚨');
  }
}

export async function getUserSubscriptionWithProducts(
  subId: Subscription['id']
): Promise<SubscriptionWithProducts> {
  try {
    const response = await cms.get(
      `/user-subscription-with-products-by-subid/${subId}`
    );
    return formatSubscriptionWithProductsResponse(response.data);
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get subscription 🚨');
  }
}

export interface AddProductsRequest {
  pricesIds: string[];
  authId: string;
  userImpersonationId: number | null;
  couponCode?: string | null;
  userProductId: number;
}

export async function addProductsToSubscription(
  subId: string,
  request: AddProductsRequest
): Promise<unknown> {
  try {
    const response = await cms.put(
      `/v2/user-subscription/${subId}/add-products`,
      request
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error?.message);
    } else {
      throw new Error('Error inesperado. Por favor, inténtalo de nuevo.');
    }
  }
}
